import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import EditSvg from "../../../../common/svgs/EditSvg";
import InfoSvg from "../../../../common/svgs/InfoSvg";
import CustomTooltip from "../common/CustomTooltip";

interface PnoeDetailsContainerProps {
  children: React.ReactNode;
}

/**
 * The single bordered box holding every detail section (Figma node
 * 16827:67296), with a hairline between them.
 *
 * The design merged what used to be three separate cards — Client,
 * Measurement details, Protocol — into one container, so the reader takes the
 * whole record in as one block rather than three.
 */
export const PnoeDetailsContainer: React.FC<PnoeDetailsContainerProps> = ({
  children,
}) => {
  const theme: Theme = useTheme();
  // `Fragment`s keep the divider out of the children's own keys.
  const sections = React.Children.toArray(children).filter(Boolean);

  return (
    <div style={styles.container(theme)}>
      {sections.map((section, index) => (
        <React.Fragment key={index}>
          {index > 0 && <div style={styles.divider(theme)} />}
          {section}
        </React.Fragment>
      ))}
    </div>
  );
};

interface PnoeDetailSectionProps {
  title: string;
  /** Field grid / plain body. Ignored when `columns` is given. */
  children?: React.ReactNode;
  /**
   * The design lays each section out as three explicit column stacks rather
   * than a row-flowing grid, and the split differs per section (Client is
   * 3/3/1, Measurement details 2/2/2, Protocol 2/1/1). Passing the columns
   * verbatim reproduces that exactly instead of guessing a rule from the
   * field count.
   */
  columns?: React.ReactNode[];
  /** Right-hand slot in the section header (badges, notes). */
  headerRight?: React.ReactNode;
  /**
   * Renders the children as-is instead of in the three-column field grid. Used
   * by the phases table, which is rows of records rather than label/value
   * fields.
   */
  plainBody?: boolean;
  /**
   * `card` is the standalone bordered card (the chart still uses it);
   * `inline` is a section inside `PnoeDetailsContainer` — no border of its
   * own, and the design's lighter section title.
   */
  variant?: "card" | "inline";
  /**
   * Shows the pencil in the section header. Omitted means the section is not
   * editable — Measurement details never is, and Protocol only on an RMR test.
   */
  onEdit?: () => void;
  /**
   * Greys the pencil out and refuses the click, with `editDisabledReason` as
   * its tooltip. Disabled rather than hidden so the affordance stays
   * discoverable — the reader learns the section *is* editable, and why not
   * now.
   */
  editDisabled?: boolean;
  editDisabledReason?: string;
}

/**
 * One section of the PNOĒ test detail: a title, an optional edit pencil, and
 * fields in a three-column grid (label above value) at every width — the
 * columns never collapse.
 */
const PnoeDetailSection: React.FC<PnoeDetailSectionProps> = ({
  title,
  children,
  columns,
  headerRight,
  plainBody = false,
  variant = "card",
  onEdit,
  editDisabled = false,
  editDisabledReason,
}) => {
  const theme: Theme = useTheme();
  const isInline = variant === "inline";

  return (
    <section style={isInline ? styles.sectionInline : styles.section(theme)}>
      <header style={styles.header}>
        <span style={isInline ? styles.titleInline(theme) : styles.title(theme)}>
          {title}
        </span>
        {headerRight && <div style={styles.headerRight}>{headerRight}</div>}
        {onEdit && (
          <PencilTooltip message={editDisabled ? editDisabledReason : null}>
            <div
              style={styles.editButton(editDisabled)}
              onClick={editDisabled ? undefined : onEdit}
              role="button"
              aria-label={`Edit ${title.toLowerCase()}`}
              aria-disabled={editDisabled}
            >
              <EditSvg
                color={
                  editDisabled
                    ? theme.colors_ds.NEUTRAL_ICON_WEAK
                    : theme.colors_ds.PRIMARY_ICON_MAIN
                }
              />
            </div>
          </PencilTooltip>
        )}
      </header>
      {columns ? (
        <div style={styles.columns}>
          {columns.map((column, index) => (
            <div key={index} style={styles.column}>
              {column}
            </div>
          ))}
        </div>
      ) : plainBody ? (
        <div style={styles.plainBody}>{children}</div>
      ) : (
        <div style={styles.grid}>{children}</div>
      )}
    </section>
  );
};

/**
 * Wraps the pencil in the platform tooltip only when there is something to
 * say. `CustomTooltip` renders a wrapping div, so an always-on wrapper would
 * add a layout box to every section header for nothing.
 */
const PencilTooltip: React.FC<{
  message: string | null | undefined;
  children: React.ReactElement;
}> = ({ message, children }) =>
  message ? (
    <CustomTooltip message={message} customHeight="auto">
      {children}
    </CustomTooltip>
  ) : (
    children
  );

interface PnoeFieldProps {
  label: string;
  /** Falsy values render an em dash rather than an empty cell. */
  value?: React.ReactNode;
  /** Lets a single field span the full row — for long free-text values. */
  fullWidth?: boolean;
  /**
   * Help text behind an info icon beside the label — the `details` a
   * configured question carries. Nothing is rendered when empty.
   */
  hint?: string;
}

/**
 * One label/value pair, label above value.
 *
 * One field per cell: the requirements call out the old page's combined
 * gender + age row as confusing, so no cell ever holds two fields.
 */
export const PnoeField: React.FC<PnoeFieldProps> = ({
  label,
  value,
  fullWidth = false,
  hint,
}) => {
  const theme: Theme = useTheme();
  return (
    <div style={fullWidth ? styles.fieldFullWidth : styles.field}>
      <span style={styles.fieldLabel(theme)}>
        {label}
        {hint && (
          <CustomTooltip message={hint} customHeight="auto">
            <span style={styles.fieldHintIcon} aria-label={hint}>
              <InfoSvg color={theme.colors_ds.NEUTRAL_ICON_WEAK} size={12} />
            </span>
          </CustomTooltip>
        )}
      </span>
      <span style={styles.fieldValue(theme)}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </span>
    </div>
  );
};

export default PnoeDetailSection;

const styles = {
  container: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  divider: (theme: Theme) => ({
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  sectionInline: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  titleInline: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
    minWidth: 0,
  }),
  editButton: (disabled: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    flexShrink: 0,
    cursor: disabled ? "not-allowed" : "pointer",
  }),
  // The chart card. The design draws it with an elevation shadow, but on this
  // page it sits beside the bordered details container, and two different
  // card treatments next to each other read as a mistake — so it takes the
  // same weak border. 24px between the header and the plot, per the design.
  section: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    minHeight: "24px",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  plainBody: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  // Three equal column stacks, as the design draws them.
  columns: {
    display: "flex",
    alignItems: "flex-start",
    gap: "24px",
  },
  column: {
    flex: "1 1 0",
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  // Always three columns. Long values wrap (see `fieldValue`) rather than the
  // grid collapsing to fewer columns.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    columnGap: "24px",
    rowGap: "16px",
  },
  field: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    minWidth: 0,
  },
  fieldFullWidth: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    minWidth: 0,
    gridColumn: "1 / -1",
  },
  fieldLabel: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  // `CustomTooltip` wraps its child in a block; the icon box keeps it inline
  // with the label text.
  fieldHintIcon: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "default",
  },
  fieldValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    wordBreak: "break-word" as "break-word",
  }),
};
